import './library.less'

import {m, MediaQuery, wrap} from '@codeurs/front'
import {Field, Form, Formik} from 'formik'
import {useList} from 'hooks/uselist'
import {useSkip} from 'hooks/useskip'
import requestService from 'mithril/request'
import {Fragment, useState} from 'react'
import {useNavigate} from 'react-router'
import {CardData, PageData, PaginationResult, Tag} from 'types'
import {NumberParam, NumericArrayParam, StringParam} from 'use-query-params'
import {t} from 'util/i18n'
import {PageLink} from 'util/pagelink'
import {useFilter} from 'util/params'
import {style} from 'util/style'
import {Layout} from 'views/layout'
import {Dropdown, DropdownItem} from 'views/partials/dropdown'
import {LoadMore} from 'views/partials/loadmore'
import {Newsletter} from 'views/partials/newsletter'
import {Theme} from 'views/theme'
import {ArticleGrid, LibraryCard} from '../partials/articlegrid'
import {HeaderPortal} from '../partials/header'
import {Hero} from '../partials/hero'
import {SearchField} from '../partials/searchfield'

const request = requestService.request

const styles = style('library', ['card', 'hero'])

const LibraryFilter = ({group, children}) => {
  group = group.replace(/^[^_]+_/g, '')
  return (
    <div className={styles('filter')}>
      <label className={styles('filter-title')}>
        {t('library.filter.' + group)}
      </label>
      <div className={styles('filter-tags')}>{children}</div>
    </div>
  )
}

const YearFilter = ({filter, year, index, separator}) => (
  <Fragment>
    <PageLink
      class={styles
        .hero('years-item')
        .is({selected: filter.get('year') === parseInt(year, 10)})}
      to={filter.set({year}).toString()}
      replace
    >
      <span class={styles.hero('years-item-placeholder')}>{year}</span>
      <div class={styles.hero('years-item-text')}>{year}</div>
    </PageLink>
    {separator && <span class={styles.hero('years-separator')} />}
  </Fragment>
)

export const Library = wrap(Layout)((props: {
  location: Location
  data: PageData & {
    documents: PaginationResult<CardData>
    tags: {[group: string]: Array<Tag>}
    types: Array<{
      id: string
      title: string
      channel: string
      count: number
    }>
    years: Array<number>
  }
}) => {
  const navigate = useNavigate()
  const {
    url,
    documents,
    types,
    title,
    channel,
    hero,
    years,
    tagslist,
    tagspath,
    newsletter,
    language
  } = props.data
  const [tags, setTags] = useState(props.data.tags)
  const filter = useFilter({
    type: NumberParam,
    year: NumberParam,
    'tag[]': NumericArrayParam,
    query: StringParam
  })
  const list = useList<CardData>({
    url: url + filter.toString(),
    data: {json: 'documents'},
    hydrate: documents,
    amounts: {
      start: 10,
      page: 12
    }
  })
  const selectedYear = filter.get('year')

  const yearSlice = years.length > 7 ? 6 : 7

  useSkip(() => {
    request(url, {
      data: {
        query: filter.get('query'),
        year: selectedYear,
        json: 'tags'
      }
    }).then(setTags)
  }, [selectedYear, filter.get('query'), channel])
  const getTagChannels = () => {
    switch (channel) {
      case 'news':
        return ['news_type']
      case 'links':
        return ['link_category', 'link_themes']
      case 'documents':
        return ['category', 'themes']
      default:
        return []
    }
  }

  const getTagItems = () => {
    const filteredTags = []
    const allowedChannels = getTagChannels()
    Object.entries(tags).forEach(([group, items]) => {
      const filteredItems = items.filter(
        i => i.count > 0 || filter.active({'tag[]': i.id})
      )
      if (allowedChannels.indexOf(group) >= 0 && filteredItems.length > 0)
        filteredTags.push([group, filteredItems])
    })
    return filteredTags
  }

  const filteredTypes = types.filter(
    ft => ft.count > 0 || filter.get('type') === ft.id
  )

  return (
    <Fragment>
      <Theme.Container>
        <HeaderPortal>
          <Hero hero={!!(hero && hero.image)} wide={true}>
            <div className={styles.hero('left')}>
              <Theme.H1>{title}</Theme.H1>
            </div>
            <div className={styles.hero('right')}>
              <Formik
                initialValues={{query: filter.get('query')}}
                onSubmit={values => {
                  filter.set({query: values.query}).apply()
                }}
              >
                <Form>
                  <SearchField white={true}>
                    <Field
                      name="query"
                      placeholder={t('library.search') + title.toLowerCase()}
                      aria-label={t('library.search') + title.toLowerCase()}
                    />
                  </SearchField>
                </Form>
              </Formik>
              {years.length ? (
                <div className={styles.hero('years-container')}>
                  <LibraryDropdown
                    filter={filter}
                    years={years}
                    yearSlice={yearSlice}
                  />
                  <ul
                    className={styles
                      .hero('years')
                      .mod({dark: !(hero && hero.image)})}
                  >
                    <li>
                      <PageLink
                        class={styles
                          .hero('years-item')
                          .is({selected: filter.get('year') === undefined})}
                        to={filter.set({year: undefined}).toString()}
                        replace
                      >
                        <span class={styles.hero('years-item-placeholder')}>
                          {t('library.all_years')}
                        </span>
                        <div class={styles.hero('years-item-text')}>
                          {t('library.all_years')}
                        </div>
                      </PageLink>
                    </li>
                    <li class={styles.hero('years-separator')} />
                    {years.slice(0, yearSlice).map((year, index) => (
                      <Fragment>
                        <li>
                          <YearFilter
                            filter={filter}
                            year={year}
                            index={index}
                            separator={false}
                          />
                        </li>
                        {index !== years.length - 1 ? (
                          <li class={styles.hero('years-separator')} />
                        ) : null}
                      </Fragment>
                    ))}
                    <MediaQuery
                      view={() => {
                        if (!years.slice(yearSlice).length) return null
                        return (
                          <li>
                            <select
                              name="year"
                              aria-label={t('library.select_year')}
                              className={styles
                                .hero('years-select')
                                .is({
                                  selected:
                                    selectedYear <= years.slice(yearSlice)[0]
                                })
                                .mod({dark: !(hero && hero.image)})}
                              onChange={e => {
                                if (e.target.value) {
                                  const newUrl = filter
                                    .set({year: e.target.value})
                                    .toString()
                                  navigate(newUrl, {replace: true})
                                }
                              }}
                              value={
                                selectedYear > years.slice(yearSlice)[0]
                                  ? ''
                                  : selectedYear
                              }
                            >
                              <option
                                className={styles.hero('years-select-option')}
                                value={''}
                                disabled
                                hidden
                                selected
                                aria-hidden="true"
                              >
                                {years.slice(yearSlice)[0]}
                              </option>
                              {years.slice(yearSlice).map(year => (
                                <option
                                  className={styles.hero('years-select-option')}
                                  value={year}
                                  aria-label={`${year}`}
                                >
                                  {year}
                                </option>
                              ))}
                            </select>
                          </li>
                        )
                      }}
                      minWidth={1024}
                    />
                  </ul>
                </div>
              ) : null}
            </div>
          </Hero>
        </HeaderPortal>
        {channel !== 'news' &&
          (filteredTypes.length > 0 || getTagItems().length > 0) && (
            <div className={styles('filters')}>
              {filteredTypes.length ? (
                <LibraryFilter group="type">
                  {filteredTypes.map(type => (
                    <PageLink
                      to={filter
                        .set({
                          type:
                            filter.get('type') === type.id ? undefined : type.id
                        })
                        .toString()}
                      className={styles('filter-tag').is({
                        selected: filter.get('type') === type.id
                      })}
                      replace
                    >
                      {type.title} ({type.count})
                    </PageLink>
                  ))}
                </LibraryFilter>
              ) : null}
              {getTagItems().map(([group, items]) => (
                <LibraryFilter group={group}>
                  {items.map(tag => (
                    <PageLink
                      to={filter.toggle({'tag[]': tag.id}).toString()}
                      className={styles('filter-tag').is({
                        selected: filter.active({'tag[]': tag.id})
                      })}
                      replace
                    >
                      {tag.tag} ({tag.count})
                    </PageLink>
                  ))}
                </LibraryFilter>
              ))}
            </div>
          )}
      </Theme.Container>
      <ArticleGrid hasMore={list.hasMore}>
        {list.items.map((item, i) => (
          <LibraryCard
            item={{key: i, ...item}}
            tagslist={tagslist}
            tagspath={tagspath}
            half={i === 0 || i === 9}
          />
        ))}
      </ArticleGrid>
      {list.hasMore && (
        <div className={styles('button')}>
          <LoadMore loading={list.loading} loadMore={() => list.loadMore()} />
        </div>
      )}
      <Newsletter {...newsletter} language={language} />
    </Fragment>
  )
})

export const LibraryDropdown: React.FC<{
  years: Array<number>
  filter: any
  yearSlice: number
}> = ({years, filter, yearSlice}) => (
  <div class={styles.hero('dropdown')}>
    <div class={styles.hero('dropdown-label')}>{t('library.title') + ':'}</div>
    <Dropdown
      title={filter.get('year') ? filter.get('year') : t('library.all_years')}
    >
      <PageLink to={filter.set({year: null}).toString()} replace>
        <DropdownItem key={t('library.all_years')}>
          {t('library.all_years')}
        </DropdownItem>
      </PageLink>
      {years.map((year, index) => (
        <PageLink to={filter.set({year}).toString()} replace>
          <DropdownItem key={index}>{year}</DropdownItem>
        </PageLink>
      ))}
    </Dropdown>
  </div>
)
